import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Logo } from '../ui/Logo';
import type { LoginRequest } from '@trading-app/shared';

export function LoginForm() {
  const { login, isLoading } = useAuth();
  const [formData, setFormData] = useState<LoginRequest>({
    email: '',
    password: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const result = await login(formData);
      if (!result.success) {
        setError(result.message || 'Login failed');
      }
      // Success will be handled by auth context and routing
    } catch (error) {
      setError('An error occurred during login');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Logo and Tagline */}
      <div className="mb-10">
        <div className="mb-4">
          <Logo className="h-10 w-32" />
        </div>
        <p className="font-inter text-lg text-black leading-[28px]">
          Exponential Returns through Intelligent Computation
        </p>
      </div>

      {/* Main Content */}
      <div className="space-y-10">
        {/* Title */}
        <div>
          <h1 className="font-inter font-semibold text-4xl text-[#171819] leading-[48px]">
            Sign In. Buy Lambo.
          </h1>
        </div>

        {/* Form */}
        <div className="space-y-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email Field */}
            <div className="space-y-2">
              <label htmlFor="email" className="block font-inter font-semibold text-label-3 text-type-primary tracking-[-0.0325px]">
                Email Address
              </label>
              <div className="relative">
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full h-12 px-4 py-3 bg-system-white border border-border-gray rounded-[4px] font-inter text-body-2 text-type-disabled placeholder:text-type-disabled focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-colors"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <label htmlFor="password" className="block font-inter font-semibold text-label-3 text-type-primary tracking-[-0.0325px]">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full h-12 px-4 py-3 bg-system-white border border-border-gray rounded-[4px] font-inter text-body-2 text-type-disabled placeholder:text-type-disabled focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-colors"
                  placeholder="Enter your password"
                />
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-[4px] p-3">
                <p className="text-sm text-red-700 font-inter">{error}</p>
              </div>
            )}

            {/* Sign In Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full h-12 bg-brand-primary text-type-light rounded-[4px] font-inter font-semibold text-button-2 hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          {/* Sign Up Link */}
          <p className="font-inter text-text-lg text-text-gray">
            <span>Don't have an account? </span>
            <Link 
              to="/register" 
              className="text-brand-primary hover:underline font-inter"
            >
              Sign Up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}